import React, { memo, useMemo } from 'react';
import { CheckCircle, Circle } from 'lucide-react';

const OptimizedProgressBar = memo(({ 
  currentSection, 
  totalSections, 
  completedSections = [],
  sectionTitles = [],
  progress: progressProp
}) => {
  const progressData = useMemo(() => {
    const progress = progressProp != null
      ? progressProp
      : (currentSection / totalSections) * 100;
    const sections = Array.from({ length: totalSections }, (_, index) => ({
      index: index + 1,
      isCompleted: completedSections.includes(index + 1),
      isCurrent: index + 1 === currentSection,
      title: sectionTitles[index] || `Section ${index + 1}`
    }));
    
    return { progress, sections };
  }, [currentSection, totalSections, completedSections, sectionTitles, progressProp]);

  return (
    <div className="space-y-3" role="progressbar" aria-valuenow={progressData.progress} aria-valuemin={0} aria-valuemax={100} aria-label={`Progress: step ${currentSection} of ${totalSections}`}>
      {/* Minimal Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-medium text-gray-700">Progress</h3>
        <span className="text-xs text-gray-500">
          {currentSection} of {totalSections}
        </span>
      </div>
      
      {/* Elegant Progress Bar with Integrated Dots */}
      <div className="relative">
        {/* Background Track */}
        <div className="overflow-hidden w-full h-2 bg-gray-100 rounded-full">
          <div 
            className="h-full bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 rounded-full shadow-sm transition-all duration-700 ease-out"
            style={{ width: `${progressData.progress}%` }}
          />
        </div>
        
        {/* Floating Section Indicators */}
        <div className="flex absolute top-0 left-0 justify-between items-center w-full h-2">
          {progressData.sections.map((section, index) => {
            // Calculate even spacing across the progress bar
            const position = totalSections === 1 ? 50 : (index / (totalSections - 1)) * 100;
            return (
              <div
                key={section.index}
                className="absolute transform -translate-x-1/2"
                style={{ left: `${position}%` }}
              >
                <div 
                  className={`w-4 h-4 rounded-full border-2 transition-all duration-300 ${
                    section.isCompleted 
                      ? 'bg-white border-blue-500 shadow-sm' 
                      : section.isCurrent 
                      ? 'bg-white border-blue-600 shadow-md scale-110' 
                      : 'bg-gray-100 border-gray-300'
                  }`}
                  title={section.title}
                >
                  {section.isCompleted && (
                    <div className="flex justify-center items-center w-full h-full">
                      <div className="w-2 h-2 bg-blue-500 rounded-full" />
                    </div>
                  )}
                  {section.isCurrent && !section.isCompleted && (
                    <div className="flex justify-center items-center w-full h-full">
                      <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
});

OptimizedProgressBar.displayName = 'OptimizedProgressBar';

export default OptimizedProgressBar;
